import React, { useState, useEffect } from "react";
import { db, auth } from "./firebase";
import { doc, onSnapshot, addDoc, collection, serverTimestamp } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import { ToastContainer, toast, Bounce } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import "./Revenue.css";

const Revenue = () => {
  const [stats, setStats] = useState({
    totalRevenue: 0,
    monthlyRevenue: 0,
    activeSubscriptions: 0,
    pendingPayout: 0,
    conversionRate: 0,
    arpu: 0,
    growth: {}
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [user, setUser] = useState(null);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [withdrawMethod, setWithdrawMethod] = useState(null);
  const [formData, setFormData] = useState({
    amount: "",
    bankName: "", // New field
    accountId: "",
    binanceId: "" // Changed from walletId
  });

  const MIN_WITHDRAWAL = 25;

  useEffect(() => {
    if (isModalOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    return () => { document.body.style.overflow = "auto"; };
  }, [isModalOpen]);

  useEffect(() => {
    const unsubscribeAuth = onAuthStateChanged(auth, (currentUser) => {
      if (currentUser) {
        setUser(currentUser);
        const userDocRef = doc(db, "users", currentUser.uid);
        const unsubscribeDoc = onSnapshot(userDocRef, (docSnap) => {
          if (docSnap.exists()) {
            const data = docSnap.data();
            setStats({
              totalRevenue: data.revenue || 0,
              monthlyRevenue: data.monthlyRevenue || 0,
              activeSubscriptions: data.activeSubscriptions || 0,
              pendingPayout: data.pendingPayout || 0,
              conversionRate: data.conversionRate || 0,
              arpu: data.arpu || 0,
              growth: data.growth || {}
            });
          }
          setLoading(false);
        });
        return () => unsubscribeDoc();
      } else {
        setLoading(false);
        setUser(null);
      }
    });
    return () => unsubscribeAuth();
  }, []);

  const handleAmountChange = (e) => {
    let value = e.target.value;
    const maxVal = stats.totalRevenue;
    if (parseFloat(value) > maxVal) {
      value = maxVal.toString();
    }
    setFormData({ ...formData, amount: value });
  };

  const handleInputChange = (e, field) => {
    const value = e.target.value;
    // Keep numeric logic only for IDs, allow text for bankName
    if (field === "accountId" || field === "binanceId") {
      const numericValue = value.replace(/\D/g, "");
      setFormData({ ...formData, [field]: numericValue });
    } else {
      setFormData({ ...formData, [field]: value });
    }
  };

  const isFormInvalid = () => {
    const amountNum = parseFloat(formData.amount);
    const isAmountValid = formData.amount && amountNum >= MIN_WITHDRAWAL;
    const hasEnoughBalance = stats.totalRevenue >= MIN_WITHDRAWAL;

    if (!hasEnoughBalance || !isAmountValid) return true;

    if (withdrawMethod === "bank") {
      return !formData.accountId || !formData.bankName;
    } else {
      return !formData.binanceId;
    }
  };

  const handleWithdrawSubmit = async (e) => {
    e.preventDefault();
    
    if (stats.totalRevenue < MIN_WITHDRAWAL) {
      toast.error(`Minimum withdrawal amount is $${MIN_WITHDRAWAL}`);
      return;
    }

    setSubmitting(true);

    try {
      await addDoc(collection(db, "payments"), {
        userId: user.uid,
        userEmail: user.email,
        withdrawalType: withdrawMethod,
        amount: parseFloat(formData.amount),
        bankName: withdrawMethod === "bank" ? formData.bankName : null,
        accountId: withdrawMethod === "bank" ? formData.accountId : formData.binanceId,
        status: "pending",
        timestamp: serverTimestamp()
      });

      toast.success(
        <div>
          <b style={{ display: 'block' }}>Success!</b>
          <span style={{ fontSize: '0.85rem' }}>
            Withdrawal of ${formData.amount} is being processed.
          </span>
        </div>, 
        {
          position: "top-right",
          autoClose: 4000,
          hideProgressBar: false,
          closeOnClick: true,
          pauseOnHover: true,
          draggable: true,
          theme: "colored",
          transition: Bounce,
        }
      );

      setIsModalOpen(false);
      setFormData({ amount: "", bankName: "", accountId: "", binanceId: "" });
      setWithdrawMethod(null);
    } catch (error) {
      toast.error("Withdrawal request failed. Please try again.", {
        position: "top-right",
        autoClose: 3000,
        theme: "dark",
      });
      console.error(error);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="loading">Loading Analytics...</div>;

  return (
    <div className="revenue-wrapper">
      <ToastContainer limit={3} newestOnTop pauseOnFocusLoss />

      <div className="revenue-header">
        <h1 className="revenue-title">Your Revenue & Analytics</h1>
        <button className="withdraw-btn-trigger" onClick={() => setIsModalOpen(true)}>
          $ Withdraw Funds
        </button>
      </div>

      <div className="revenue-grid">
        <div className="revenue-card">
          <span className="card-label">ESTIMATED EARNINGS</span>
          <h2>${Number(stats.totalRevenue).toLocaleString()}</h2>
          <span className="growth positive">{stats.growth?.total || "+0%"}</span>
        </div>
        <div className="revenue-card">
          <span className="card-label">PAGE RPM</span>
          <h2>${Number(stats.monthlyRevenue).toLocaleString()}</h2>
          <span className="growth positive">{stats.growth?.monthly || "+0%"}</span>
        </div>
        <div className="revenue-card">
          <span className="card-label">PAGE VIEWS</span>
          <h2>{Number(stats.activeSubscriptions).toLocaleString()}</h2>
          <span className="growth positive">{stats.growth?.subs || "+0"}</span>
        </div>
        <div className="revenue-card">
          <span className="card-label">CLICKS</span>
          <h2>{Number(stats.pendingPayout).toLocaleString()}</h2>
          <span className="growth neutral">Live</span>
        </div>
        <div className="revenue-card">
          <span className="card-label">PAGE CTR</span>
          <h2>{stats.conversionRate}%</h2>
          <span className="growth positive">{stats.growth?.conversion || "0%"}</span>
        </div>
        <div className="revenue-card">
          <span className="card-label">AD IMPRESSIONS</span>
          <h2>{Number(stats.arpu).toLocaleString()}</h2>
          <span className="growth positive">{stats.growth?.arpu || "+0%"}</span>
        </div>
      </div>

      {isModalOpen && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-nav-header">
              {withdrawMethod && (
                <button className="back-btn-top" onClick={() => setWithdrawMethod(null)}>
                  ← Back
                </button>
              )}
              <button className="close-modal-btn" onClick={() => {setIsModalOpen(false); setWithdrawMethod(null)}}>×</button>
            </div>
            
            {!withdrawMethod ? (
              <div className="method-selection">
                <h3>Select Withdrawal Method</h3>
                <div className="method-options">
                  <div className="method-card" onClick={() => setWithdrawMethod('bank')}>
                    <div className="icon">🏦</div>
                    <span>Bank Transfer</span>
                  </div>
                  <div className="method-card" onClick={() => setWithdrawMethod('binance')}>
                    <div className="icon">🟡</div>
                    <span>Binance (BEP20/TRC20)</span>
                  </div>
                </div>
              </div>
            ) : (
              <div className="withdraw-form-container">
                <h3 className="form-title-main">
                    {withdrawMethod === 'bank' ? 'Bank Details' : 'Binance Details'}
                </h3>
                
                <form onSubmit={handleWithdrawSubmit}>
                  <div className="input-group">
                    <label>Email (Auto)</label>
                    <input type="text" value={user?.email} disabled className="disabled-input" />
                  </div>

                  {withdrawMethod === 'bank' ? (
                    <>
                      <div className="input-group">
                        <label>Bank Name</label>
                        <input 
                          type="text" 
                          placeholder="e.g. Chase Bank" 
                          required 
                          value={formData.bankName}
                          onChange={(e) => handleInputChange(e, 'bankName')} 
                        />
                      </div>
                      <div className="input-group">
                        <label>Bank Account Number (Numbers Only)</label>
                        <input 
                          type="text" 
                          placeholder="e.g. 123456789" 
                          required 
                          value={formData.accountId}
                          onChange={(e) => handleInputChange(e, 'accountId')} 
                        />
                      </div>
                    </>
                  ) : (
                    <div className="input-group">
                      <label>Binance ID / Wallet Address</label>
                      <input 
                        type="text" 
                        placeholder="Enter your Binance ID" 
                        required 
                        value={formData.binanceId}
                        onChange={(e) => handleInputChange(e, 'binanceId')} 
                      />
                    </div>
                  )}

                  <div className="input-group">
                    <label>Amount to Withdraw ($)</label>
                    <input 
                        type="number" 
                        placeholder="0.00" 
                        max={stats.totalRevenue} 
                        value={formData.amount}
                        step="0.01"
                        required 
                        onChange={handleAmountChange} 
                    />
                    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: '5px' }}>
                        <small className="balance-text">Available: ${Number(stats.totalRevenue).toLocaleString()}</small>
                        <small style={{ color: stats.totalRevenue < 25 ? '#ff4d4d' : '#aaa' }}>Min: $25.00</small>
                    </div>
                  </div>

                  <button 
                    type="submit" 
                    className="submit-withdraw-btn"
                    disabled={isFormInvalid() || submitting}
                  >
                    {submitting ? "Processing..." : 
                     stats.totalRevenue < MIN_WITHDRAWAL ? `Minimum $${MIN_WITHDRAWAL} required` :
                     isFormInvalid() ? "Please Fill All Fields" : "Confirm Withdrawal"}
                  </button>
                </form>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Revenue;